import { Formik, useField } from "formik";
import { Heart, Phone, UserRound } from "lucide-react";
import { useState } from "react";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { DateField, PhoneField, SsnField } from "@/components/common/masked-form-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type {
  Client,
  ContactMethod,
  MaritalStatus,
  UpdateClientPayload,
} from "@/features/clients/types";
import { formatPhone, isoToMaskedDate, onlyDigits, SSN_RE } from "@/lib/masks";
import {
  contactAddressInitialValues,
  contactAddressPayload,
  contactAddressRules,
  type ContactAddressValues,
  dateSchema,
  dateToPayload,
  FIELD_LABEL,
  optionalMatch,
  phoneRule,
  type ProfileFormProps,
  useProfileSubmit,
} from "@/features/clients/components/profile-form-shared";
import {
  AddressSection,
  ContactMethodField,
  NotesSection,
  ProfileFormShell,
} from "@/features/clients/components/profile-form-sections";

const sections = [
  { id: "client-details", label: "Client Details" },
  { id: "spouse-information", label: "Spouse Information" },
  { id: "contact-details", label: "Contact Details" },
  { id: "address", label: "Residential & Mailing Address" },
  { id: "notes", label: "Notes" },
];

/** Individual-specific values on top of the shared contact/address slice. */
interface IndividualProfileFormValues extends ContactAddressValues {
  firstName: string;
  middleInitial: string;
  lastName: string;
  /** SSN/ITIN — blank means "keep the stored value". */
  taxId: string;
  dateOfBirth: string;
  driverLicense: string;
  licenseState: string;
  passportNumber: string;
  internalReferenceId: string;
  salutation: string;
  isDeceased: boolean;
  dateOfDeath: string;
  trusteeName: string;
  trusteeRole: string;
  maritalStatus: MaritalStatus;
  dateOfMarriage: string;
  inCommunityPropertyState: boolean;
  spouseFirstName: string;
  spouseMiddleInitial: string;
  spouseLastName: string;
  spouseSsn: string;
  spouseDateOfBirth: string;
  spouseDriverLicense: string;
  spouseLicenseState: string;
  spousePassportNumber: string;
  spouseSalutation: string;
  spouseIsDeceased: boolean;
  spouseDateOfDeath: string;
  spouseTrusteeName: string;
  spouseTrusteeRole: string;
  spouseHomePhone: string;
  spouseMobilePhone: string;
  spouseFaxNumber: string;
  spouseWorkPhone: string;
  spouseWorkMobile: string;
  spousePreferredContactMethod: ContactMethod;
  spouseEmail: string;
}

function toInitialValues(client: Client): IndividualProfileFormValues {
  return {
    ...contactAddressInitialValues(client),
    firstName: client.firstName ?? "",
    middleInitial: client.middleInitial ?? "",
    lastName: client.lastName ?? "",
    taxId: "",
    dateOfBirth: isoToMaskedDate(client.dateOfBirth ?? ""),
    driverLicense: client.driverLicense ?? "",
    licenseState: client.licenseState ?? "",
    passportNumber: client.passportNumber ?? "",
    internalReferenceId: client.internalReferenceId ?? "",
    salutation: client.salutation ?? "",
    isDeceased: client.isDeceased ?? false,
    dateOfDeath: isoToMaskedDate(client.dateOfDeath ?? ""),
    trusteeName: client.trusteeName ?? "",
    trusteeRole: client.trusteeRole ?? "",
    maritalStatus: client.maritalStatus ?? "",
    dateOfMarriage: isoToMaskedDate(client.dateOfMarriage ?? ""),
    inCommunityPropertyState: client.inCommunityPropertyState ?? false,
    spouseFirstName: client.spouseFirstName ?? "",
    spouseMiddleInitial: client.spouseMiddleInitial ?? "",
    spouseLastName: client.spouseLastName ?? "",
    spouseSsn: "",
    spouseDateOfBirth: isoToMaskedDate(client.spouseDateOfBirth ?? ""),
    spouseDriverLicense: client.spouseDriverLicense ?? "",
    spouseLicenseState: client.spouseLicenseState ?? "",
    spousePassportNumber: client.spousePassportNumber ?? "",
    spouseSalutation: client.spouseSalutation ?? "",
    spouseIsDeceased: client.spouseIsDeceased ?? false,
    spouseDateOfDeath: isoToMaskedDate(client.spouseDateOfDeath ?? ""),
    spouseTrusteeName: client.spouseTrusteeName ?? "",
    spouseTrusteeRole: client.spouseTrusteeRole ?? "",
    spouseHomePhone: formatPhone(client.spouseHomePhone ?? ""),
    spouseMobilePhone: formatPhone(client.spouseMobilePhone ?? ""),
    spouseFaxNumber: formatPhone(client.spouseFaxNumber ?? ""),
    spouseWorkPhone: formatPhone(client.spouseWorkPhone ?? ""),
    spouseWorkMobile: formatPhone(client.spouseWorkMobile ?? ""),
    spousePreferredContactMethod: client.spousePreferredContactMethod ?? "",
    spouseEmail: client.spouseEmail ?? "",
  };
}

/** Form values → PATCH body. The tax id is omitted when blank so a stored SSN is
 *  never blanked (the API can't echo the raw value back to prefill it). */
function toPayload(values: IndividualProfileFormValues): UpdateClientPayload {
  const payload: UpdateClientPayload = {
    ...contactAddressPayload(values),
    first_name: values.firstName,
    middle_initial: values.middleInitial,
    last_name: values.lastName,
    date_of_birth: dateToPayload(values.dateOfBirth),
    driver_license: values.driverLicense,
    license_state: values.licenseState,
    passport_number: values.passportNumber,
    internal_reference_id: values.internalReferenceId,
    salutation: values.salutation,
    marital_status: values.maritalStatus,
    date_of_marriage: dateToPayload(values.dateOfMarriage),
    in_community_property_state: values.inCommunityPropertyState,
    is_deceased: values.isDeceased,
    date_of_death: dateToPayload(values.dateOfDeath),
    trustee_name: values.trusteeName,
    trustee_role: values.trusteeRole,

    spouse_first_name: values.spouseFirstName,
    spouse_middle_initial: values.spouseMiddleInitial,
    spouse_last_name: values.spouseLastName,
    spouse_date_of_birth: dateToPayload(values.spouseDateOfBirth),
    spouse_driver_license: values.spouseDriverLicense,
    spouse_license_state: values.spouseLicenseState,
    spouse_passport_number: values.spousePassportNumber,
    spouse_salutation: values.spouseSalutation,
    spouse_is_deceased: values.spouseIsDeceased,
    spouse_date_of_death: dateToPayload(values.spouseDateOfDeath),
    spouse_trustee_name: values.spouseTrusteeName,
    spouse_trustee_role: values.spouseTrusteeRole,
    spouse_home_phone: onlyDigits(values.spouseHomePhone),
    spouse_mobile_phone: onlyDigits(values.spouseMobilePhone),
    spouse_fax_number: onlyDigits(values.spouseFaxNumber),
    spouse_work_phone: onlyDigits(values.spouseWorkPhone),
    spouse_work_mobile: onlyDigits(values.spouseWorkMobile),
    spouse_preferred_contact_method: values.spousePreferredContactMethod,
    spouse_email: values.spouseEmail,
  };

  // Only include tax ids the user actually typed — sending "" would blank a real
  // stored SSN (the API only ever returns it masked, so it can't be prefilled).
  if (values.taxId) payload.ssn_tin = values.taxId;
  if (values.spouseSsn) payload.spouse_ssn = values.spouseSsn;
  return payload;
}

/** String rule that additionally becomes required when marital status is Married. */
function requiredWhenMarried(base: Yup.StringSchema, message: string) {
  return base.when("maritalStatus", { is: "MARRIED", then: (s) => s.required(message) });
}

function buildSchema(spouseIdOnFile: boolean) {
  // The spouse SSN is never prefilled (the API only echoes it masked), so an
  // existing married client already has one on file — requiring a value there
  // would force re-entry on every save. Only require it when none is on file yet.
  const spouseSsnSchema = spouseIdOnFile
    ? optionalMatch(SSN_RE, "Enter a valid SSN/ITIN")
    : requiredWhenMarried(
        optionalMatch(SSN_RE, "Enter a valid SSN/ITIN"),
        "Spouse SSN/ITIN is required",
      );
  return Yup.object({
    ...contactAddressRules,
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    taxId: optionalMatch(SSN_RE, "Enter a valid SSN/ITIN"),
    spouseFirstName: requiredWhenMarried(Yup.string(), "Spouse first name is required"),
    spouseLastName: requiredWhenMarried(Yup.string(), "Spouse last name is required"),
    spouseSsn: spouseSsnSchema,
    licenseState: optionalMatch(/^[A-Za-z]{2}$/, "Use the 2-letter state code"),
    spouseLicenseState: optionalMatch(/^[A-Za-z]{2}$/, "Use the 2-letter state code"),
    dateOfBirth: dateSchema,
    dateOfDeath: dateSchema,
    dateOfMarriage: requiredWhenMarried(dateSchema, "Date of marriage is required"),
    spouseDateOfBirth: requiredWhenMarried(dateSchema, "Spouse date of birth is required"),
    spouseDateOfDeath: dateSchema,
    spouseHomePhone: phoneRule(),
    spouseMobilePhone: phoneRule(),
    spouseFaxNumber: phoneRule("Enter a valid fax number"),
    spouseWorkPhone: phoneRule(),
    spouseWorkMobile: phoneRule(),
    spouseEmail: Yup.string().email("Enter a valid email"),
  });
}

/** Deceased checkbox + conditionally-revealed date/trustee fields. */
function DeceasedFields({
  checkboxName,
  dateOfDeathName,
  trusteeNameName,
  trusteeRoleName,
  checked,
}: {
  checkboxName: string;
  dateOfDeathName: string;
  trusteeNameName: string;
  trusteeRoleName: string;
  checked: boolean;
}) {
  const [field, , helpers] = useField<boolean>(checkboxName);
  return (
    <div className="flex flex-col gap-4 border-t p-6 sm:flex-row sm:items-start">
      <div className="flex items-center gap-2">
        <Checkbox
          id={checkboxName}
          checked={field.value}
          onCheckedChange={(v) => helpers.setValue(v === true)}
        />
        <Label htmlFor={checkboxName} className="text-muted-foreground font-normal">
          Deceased
        </Label>
      </div>
      {checked && (
        <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3">
          <DateField name={dateOfDeathName} label="Date of Death" labelClassName={FIELD_LABEL} />
          <FormField
            name={trusteeNameName}
            label="Trustee / Executor Name"
            labelClassName={FIELD_LABEL}
          />
          <FormField
            name={trusteeRoleName}
            label="Trustee / Executor Role"
            labelClassName={FIELD_LABEL}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Marital-status radio. Demoting a client who has a spouse on file from Married
 * to Unmarried is destructive — on save the server clears all spouse data and
 * deletes spouse employers — so it confirms first (legacy `unmarry.php`).
 * `hasSpouseOnFile` is true only when the client loaded already married.
 */
function SpouseMaritalStatus({ hasSpouseOnFile }: { hasSpouseOnFile: boolean }) {
  const [field, , helpers] = useField<MaritalStatus>("maritalStatus");
  const [confirmOpen, setConfirmOpen] = useState(false);

  function handleChange(next: MaritalStatus) {
    // Only guard the Married→Unmarried demotion when spouse data actually exists.
    if (next === "UNMARRIED" && field.value === "MARRIED" && hasSpouseOnFile) {
      setConfirmOpen(true);
      return;
    }
    helpers.setValue(next);
  }

  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL}>Marital Status</Label>
      <RadioGroup
        value={field.value}
        onValueChange={(v) => handleChange(v as MaritalStatus)}
        className="flex items-center gap-6 pt-1.5"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="MARRIED" id="marital-married" />
          <Label htmlFor="marital-married" className="font-normal">
            Married
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="UNMARRIED" id="marital-unmarried" />
          <Label htmlFor="marital-unmarried" className="font-normal">
            Unmarried
          </Label>
        </div>
      </RadioGroup>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove spouse information?</AlertDialogTitle>
            <AlertDialogDescription>
              Marking this client unmarried will permanently clear all spouse details and remove the
              spouse's employers when you save. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep married</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                helpers.setValue("UNMARRIED");
                setConfirmOpen(false);
              }}
            >
              Mark unmarried
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** "Community property state" flag — informational; affects later income splits. */
function CommunityPropertyCheckbox() {
  const [field, , helpers] = useField<boolean>("inCommunityPropertyState");
  return (
    <div className="flex items-center gap-2 pt-6">
      <Checkbox
        id="inCommunityPropertyState"
        checked={field.value}
        onCheckedChange={(v) => helpers.setValue(v === true)}
      />
      <Label htmlFor="inCommunityPropertyState" className="text-muted-foreground font-normal">
        Resides in a community property state
      </Label>
    </div>
  );
}

/**
 * Individual client profile form. Formik owns state/submission, Yup owns
 * validation, `FormField`/masked fields own presentation. Sensitive values
 * (SSN, DOB, PII) live in Formik state only and are never logged.
 */
export function IndividualProfileForm({
  client,
  onClientUpdated,
  extraSections,
}: ProfileFormProps) {
  const { handleSubmit, continueRef } = useProfileSubmit<IndividualProfileFormValues>({
    client,
    onClientUpdated,
    toPayload,
    toInitialValues,
    fieldMap: { ssn_tin: "taxId" },
  });

  return (
    <Formik<IndividualProfileFormValues>
      initialValues={toInitialValues(client)}
      validationSchema={buildSchema(Boolean(client.spouse?.idLabel))}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, resetForm }) => {
        const isMarried = values.maritalStatus === "MARRIED";
        return (
          <ProfileFormShell
            sections={sections}
            isSubmitting={isSubmitting}
            resetForm={resetForm}
            continueRef={continueRef}
            footer={extraSections}
          >
            {/* Client Details */}
            <Card id="client-details" className="scroll-mt-24 gap-0 overflow-hidden p-0">
              <div className="bg-muted/40 border-b px-6 py-4">
                <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
                  <UserRound className="text-primary size-5" /> Client Details
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
                <FormField name="firstName" label="First Name" labelClassName={FIELD_LABEL} />
                <FormField
                  name="middleInitial"
                  label="Middle Initial"
                  labelClassName={FIELD_LABEL}
                  maxLength={1}
                />
                <FormField name="lastName" label="Last Name" labelClassName={FIELD_LABEL} />
                <div>
                  <SsnField name="taxId" label="Tax ID Number" labelClassName={FIELD_LABEL} />
                  <p className="text-muted-foreground mt-1 text-xs">
                    Current: {client.idLabel || "—"}. Leave blank to keep unchanged.
                  </p>
                </div>
                <DateField name="dateOfBirth" label="Date of Birth" labelClassName={FIELD_LABEL} />
                <FormField
                  name="driverLicense"
                  label="Driver's License #"
                  labelClassName={FIELD_LABEL}
                />
                <FormField
                  name="licenseState"
                  label="License State"
                  labelClassName={FIELD_LABEL}
                  maxLength={2}
                />
                <FormField
                  name="passportNumber"
                  label="Passport on File"
                  labelClassName={FIELD_LABEL}
                />
                <FormField
                  name="internalReferenceId"
                  label="Internal Reference ID"
                  labelClassName={FIELD_LABEL}
                />
                <FormField
                  name="salutation"
                  label="Preferred Salutation"
                  labelClassName={FIELD_LABEL}
                />
              </div>
              <DeceasedFields
                checkboxName="isDeceased"
                dateOfDeathName="dateOfDeath"
                trusteeNameName="trusteeName"
                trusteeRoleName="trusteeRole"
                checked={values.isDeceased}
              />
            </Card>

            {/* Spouse Information */}
            <Card id="spouse-information" className="scroll-mt-24 gap-0 overflow-hidden p-0">
              <div className="bg-muted/40 border-b px-6 py-4">
                <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
                  <Heart className="text-primary size-5" /> Spouse Information
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                <SpouseMaritalStatus hasSpouseOnFile={client.maritalStatus === "MARRIED"} />
                {isMarried && (
                  <DateField
                    name="dateOfMarriage"
                    label="Date of Marriage"
                    labelClassName={FIELD_LABEL}
                  />
                )}
                {isMarried && <CommunityPropertyCheckbox />}
              </div>

              {isMarried ? (
                <>
                  <div className="border-t px-6 py-3">
                    <h4 className={FIELD_LABEL}>Spouse Details</h4>
                  </div>
                  <div className="grid grid-cols-1 gap-6 p-6 pt-0 md:grid-cols-2 lg:grid-cols-3">
                    <FormField
                      name="spouseFirstName"
                      label="First Name"
                      labelClassName={FIELD_LABEL}
                    />
                    <FormField
                      name="spouseMiddleInitial"
                      label="Middle Initial"
                      labelClassName={FIELD_LABEL}
                      maxLength={1}
                    />
                    <FormField
                      name="spouseLastName"
                      label="Last Name"
                      labelClassName={FIELD_LABEL}
                    />
                    <div>
                      <SsnField
                        name="spouseSsn"
                        label="Spouse Tax ID Number"
                        labelClassName={FIELD_LABEL}
                      />
                      <p className="text-muted-foreground mt-1 text-xs">
                        Current: {client.spouse?.idLabel || "—"}. Leave blank to keep unchanged.
                      </p>
                    </div>
                    <DateField
                      name="spouseDateOfBirth"
                      label="Date of Birth"
                      labelClassName={FIELD_LABEL}
                    />
                    <FormField
                      name="spouseDriverLicense"
                      label="Driver's License #"
                      labelClassName={FIELD_LABEL}
                    />
                    <FormField
                      name="spouseLicenseState"
                      label="License State"
                      labelClassName={FIELD_LABEL}
                      maxLength={2}
                    />
                    <FormField
                      name="spousePassportNumber"
                      label="Passport on File"
                      labelClassName={FIELD_LABEL}
                    />
                    <FormField
                      name="spouseSalutation"
                      label="Preferred Salutation"
                      labelClassName={FIELD_LABEL}
                    />
                  </div>
                  <DeceasedFields
                    checkboxName="spouseIsDeceased"
                    dateOfDeathName="spouseDateOfDeath"
                    trusteeNameName="spouseTrusteeName"
                    trusteeRoleName="spouseTrusteeRole"
                    checked={values.spouseIsDeceased}
                  />
                </>
              ) : (
                <p className="text-muted-foreground border-t p-6 text-sm">
                  Spouse details apply once marital status is set to Married.
                </p>
              )}
            </Card>

            {/* Contact Details */}
            <Card id="contact-details" className="scroll-mt-24 gap-0 overflow-hidden p-0">
              <div className="bg-muted/40 border-b px-6 py-4">
                <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
                  <Phone className="text-primary size-5" /> Contact Details
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
                <PhoneField name="homePhone" label="Home Phone" labelClassName={FIELD_LABEL} />
                <PhoneField name="mobilePhone" label="Mobile Phone" labelClassName={FIELD_LABEL} />
                <PhoneField name="faxNumber" label="Fax Number" labelClassName={FIELD_LABEL} />
                <PhoneField name="workPhone" label="Work Phone" labelClassName={FIELD_LABEL} />
                <PhoneField name="workMobile" label="Work Mobile" labelClassName={FIELD_LABEL} />
                <ContactMethodField
                  name="preferredContactMethod"
                  label="Preferred Contact Method"
                />
                <FormField
                  name="email"
                  label="Email Address"
                  type="email"
                  labelClassName={FIELD_LABEL}
                />
              </div>
              {isMarried && (
                <>
                  <div className="border-t px-6 py-3">
                    <h4 className={FIELD_LABEL}>Spouse Contact</h4>
                  </div>
                  <div className="grid grid-cols-1 gap-6 p-6 pt-0 md:grid-cols-2 lg:grid-cols-3">
                    <PhoneField
                      name="spouseHomePhone"
                      label="Spouse Home Phone"
                      labelClassName={FIELD_LABEL}
                    />
                    <PhoneField
                      name="spouseMobilePhone"
                      label="Spouse Mobile Phone"
                      labelClassName={FIELD_LABEL}
                    />
                    <PhoneField
                      name="spouseFaxNumber"
                      label="Spouse Fax Number"
                      labelClassName={FIELD_LABEL}
                    />
                    <PhoneField
                      name="spouseWorkPhone"
                      label="Spouse Work Phone"
                      labelClassName={FIELD_LABEL}
                    />
                    <PhoneField
                      name="spouseWorkMobile"
                      label="Spouse Work Mobile"
                      labelClassName={FIELD_LABEL}
                    />
                    <ContactMethodField
                      name="spousePreferredContactMethod"
                      label="Spouse Preferred Contact Method"
                    />
                    <FormField
                      name="spouseEmail"
                      label="Spouse Email Address"
                      type="email"
                      labelClassName={FIELD_LABEL}
                    />
                  </div>
                </>
              )}
            </Card>

            <AddressSection />

            <NotesSection />
          </ProfileFormShell>
        );
      }}
    </Formik>
  );
}
